import { useTranslation } from '@/i18n/useTranslation';

import type { ReactNode } from 'react';
import type { UserRole } from '@/types';

export interface RoleGuardProps {
  /** Roles permitted to view the guarded content, e.g. ['admin']. */
  allowedRoles: UserRole[];
  /** Caller-resolved effective role, or null/undefined while unknown. */
  userRole: UserRole | null | undefined;
  /** Shown while userRole is still unresolved (e.g. profile still loading). */
  isLoading?: boolean;
  loadingFallback?: ReactNode;
  /** Shown when userRole is resolved but not permitted. Defaults to <ForbiddenFallback/>. */
  forbiddenFallback?: ReactNode;
  children: ReactNode;
}

function DefaultForbidden() {
  const { t } = useTranslation();
  return (
    <div role="alert" className="forbidden-panel">
      <h1>{t('403 — Forbidden')}</h1>
      <p>
        {t(
          "You don't have permission to view this page. Contact an administrator if you believe this is an error.",
        )}
      </p>
    </div>
  );
}

/**
 * Pure, presentational role gate: no router/query dependencies, so it
 * is trivially unit-testable (see tests/RoleGuard.test.tsx). Real routes
 * wire this up to the live role via <AdminRoute> (src/auth/AdminRoute.tsx),
 * which pulls `role` from useAuth().
 *
 * IMPORTANT: this is UX convenience only. The backend independently rejects
 * every admin endpoint for non-admin roles with 403 regardless of what the
 * frontend renders (docs/AGENT_PROMPT.md §3).
 */
export function RoleGuard({
  allowedRoles,
  userRole,
  isLoading = false,
  loadingFallback = null,
  forbiddenFallback,
  children,
}: RoleGuardProps) {
  if (isLoading && !userRole) {
    return <>{loadingFallback}</>;
  }
  if (!userRole || !allowedRoles.includes(userRole)) {
    return <>{forbiddenFallback ?? <DefaultForbidden />}</>;
  }
  return <>{children}</>;
}
